/**
 * Client twin of the server's ODBC metacharacter refusal (lara-data-plane,
 * Steve 2026-10-22).
 *
 * Host, database, username and password go into an ODBC connection string
 * unescaped, so `;` would add a keyword (`Pwd=x;Server=10.0.0.5`) and `{` / `}`
 * wrap one. The server refuses them with a 422 on:
 *
 *   - `POST /database/test-connection` and `POST /database/discover-tables`
 *     (b2c, `_refuse_odbc_metacharacters` in `business2cloud/api/v1/database.py`),
 *     unless `type` is MySql, Postgres or Firebird: those drivers take the
 *     values as arguments, where the characters are inert;
 *   - `POST /sql-rag/test-connection` (KB Database, SQL Server only), always
 *     (`agentic/src/app/api/sql_rag.py`);
 *   - MyApp's and Reporting's `POST /test-connection`, always
 *     (`_refuse_odbc_metacharacters` in `myapp/api/v1/health.py` and
 *     `reporting/api/v1/reporting.py`).
 *
 * The message matches the server's `"<field> must not contain ';', '{' or '}'"`,
 * with a human label in place of the wire field name. The server stays the
 * final gate; this only says so as the user types.
 */

/** Characters that end or wrap an ODBC keyword value. */
export const ODBC_METACHARACTERS = [";", "{", "}"] as const;

/** Driver types that take the values as arguments, not an ODBC string. */
export const ARGUMENT_DRIVER_TYPES = ["MySql", "Postgres", "Firebird"] as const;

export type OdbcConnectionField = "host" | "database" | "username" | "password";

export const ODBC_FIELD_LABELS: Record<OdbcConnectionField, string> = {
  host: "Host / Server",
  database: "Database",
  username: "Username",
  password: "Password",
};

/**
 * True when the server builds an ODBC string from the values for this type.
 * Unknown or missing types are treated as SQL Server, as the server does
 * (its `type` defaults to SqlServer). Compared exactly, as the server does.
 */
export function usesOdbcConnectionString(type?: string | null): boolean {
  return !(ARGUMENT_DRIVER_TYPES as readonly string[]).includes(type ?? "");
}

/** The message for one field's value, or null when the server would accept it. */
export function odbcFieldError(
  field: OdbcConnectionField,
  value: string,
  type?: string | null,
): string | null {
  if (!usesOdbcConnectionString(type)) return null;
  if (!ODBC_METACHARACTERS.some((c) => value.includes(c))) return null;
  return `${ODBC_FIELD_LABELS[field]} must not contain ';', '{' or '}'`;
}

export type OdbcFieldErrors = Record<OdbcConnectionField, string | null>;

/** Every field's message (or null), for a form that renders all four. */
export function odbcFieldErrors(
  values: Record<OdbcConnectionField, string>,
  type?: string | null,
): OdbcFieldErrors {
  return {
    host: odbcFieldError("host", values.host, type),
    database: odbcFieldError("database", values.database, type),
    username: odbcFieldError("username", values.username, type),
    password: odbcFieldError("password", values.password, type),
  };
}

export function hasOdbcFieldError(errors: OdbcFieldErrors): boolean {
  return Object.values(errors).some((message) => message !== null);
}

/** An input's attributes for its field message (ui-standards §3 rule 1). */
export function odbcInputProps(
  errorId: string,
  message: string | null | undefined,
): { "aria-invalid"?: true; "aria-describedby"?: string } {
  return message ? { "aria-invalid": true, "aria-describedby": errorId } : {};
}
